import { useCallback, useEffect, useRef, useState } from 'react';

interface ResizeDividerProps {
  onResize: (deltaX: number) => void;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
  onDoubleClick?: () => void;
}

function DragHandleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 6 20" fill="currentColor">
      {/* 6 dots in 2 columns, 3 rows */}
      <circle cx="1.5" cy="4" r="1.5" />
      <circle cx="4.5" cy="4" r="1.5" />
      <circle cx="1.5" cy="10" r="1.5" />
      <circle cx="4.5" cy="10" r="1.5" />
      <circle cx="1.5" cy="16" r="1.5" />
      <circle cx="4.5" cy="16" r="1.5" />
    </svg>
  );
}

export function ResizeDivider({
  onResize,
  onResizeStart,
  onResizeEnd,
  onDoubleClick,
}: ResizeDividerProps) {
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      startXRef.current = e.clientX;
      setIsDragging(true);
      onResizeStart?.();
      document.body.classList.add('resizing');
    },
    [onResizeStart]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      const deltaX = e.clientX - startXRef.current;
      startXRef.current = e.clientX;
      onResize(deltaX);
    },
    [isDragging, onResize]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    onResizeEnd?.();
    document.body.classList.remove('resizing');
  }, [isDragging, onResizeEnd]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      className={`relative flex-shrink-0 w-3 h-full cursor-col-resize group flex items-center justify-center ${
        isDragging ? 'bg-primary-100' : 'bg-gray-100 hover:bg-gray-200'
      } transition-colors duration-150`}
      onMouseDown={handleMouseDown}
      onDoubleClick={onDoubleClick}
    >
      {/* Larger hit area for easier grabbing */}
      <div className="absolute inset-y-0 -left-1 -right-1" />
      {/* Drag handle indicator */}
      <div
        className={`flex items-center justify-center w-3 h-8 rounded-sm transition-all duration-150 ${
          isDragging
            ? 'bg-primary-200'
            : 'bg-transparent group-hover:bg-gray-300'
        }`}
      >
        <DragHandleIcon
          className={`w-1.5 h-5 transition-colors duration-150 ${
            isDragging
              ? 'text-primary-600'
              : 'text-gray-400 group-hover:text-gray-500'
          }`}
        />
      </div>
    </div>
  );
}
